import type { RenderStateToonOutline } from "@novorender/api";
import { useEffect } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectIsProcoreDesign } from "slices/explorer";
import { ViewMode } from "types/misc";

import { selectAdvanced, selectIsXRayActive, selectPicker, selectPoints, selectViewMode } from "../renderSlice";
import { Picker } from "../types";

export function useHandleAdvancedSettings() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const settings = useAppSelector(selectAdvanced);
    const pointSize = useAppSelector(selectPoints).size;
    const picker = useAppSelector(selectPicker);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isSectionPlanePickerActive = picker === Picker.SectionPlane;
    const viewMode = useAppSelector(selectViewMode);
    const keepOnOutlinesDuringOrthoMove = isProcoreDesign ? false : viewMode === ViewMode.CrossSection;

    // Update different settings separately to keep modified render state minimal.
    // Internal checks inside View may check object references, so no need to update unrelated properties
    // more often than needed.
    useEffect(() => {
        view?.modifyRenderState({
            output: {
                samplesMSAA: settings.msaa.enabled ? settings.msaa.samples : 0,
            },
        });
    }, [view, settings.msaa]);

    useEffect(() => {
        view?.modifyRenderState({
            tonemapping: settings.tonemapping,
        });
    }, [view, settings.tonemapping]);

    useEffect(() => {
        view?.modifyRenderState({
            pick: settings.pick,
        });
    }, [view, settings.pick]);

    useEffect(() => {
        view?.modifyRenderState({
            debug: settings.debug,
        });
    }, [view, settings.debug]);

    useEffect(() => {
        view?.modifyRenderState({
            outlines: {
                breakingPointAngleThreshold: settings.outlines.breakingPointAngleThreshold,
                showVertices: !isProcoreDesign,
                keepOnDuringOrthoMove: keepOnOutlinesDuringOrthoMove,
            },
        });
    }, [view, settings.outlines.breakingPointAngleThreshold, isProcoreDesign, keepOnOutlinesDuringOrthoMove]);

    useEffect(() => {
        if (!view) {
            return;
        }

        let toonOutline: Partial<RenderStateToonOutline>;
        if (isXRayActive) {
            toonOutline = {
                ...settings.toonOutline,
                enabled: false,
                on: false,
                outlineObjects: false,
            };
        } else if (isSectionPlanePickerActive) {
            toonOutline = { ...settings.toonOutline, enabled: true, on: true, outlineObjects: true };
        } else {
            toonOutline = { ...settings.toonOutline, on: false };
        }

        view.modifyRenderState({ toonOutline });
    }, [view, settings.toonOutline, isXRayActive, isSectionPlanePickerActive]);

    useEffect(() => {
        view?.modifyRenderState({
            points: {
                size: pointSize,
            },
        });
    }, [view, pointSize]);
}
